import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./releaseProperties.css";
import { BsEmojiGrin } from "react-icons/bs";
import { baseUrl } from "../../../services/api";
import {
  updatePropertie,
  listProperties,
  deletePropertie,
} from "../../../services/api/properties";
import { listRealEstates } from "../../../services/api/real_estates";
import { useProperty } from "../../../utils/PropertyContext";
import { FaRegTrashAlt, FaPlus, FaRegCopy } from "react-icons/fa";
import { formatMoney, formatCentsToBRL } from "../../../utils/formatMoney";
import { Dropdown } from "semantic-ui-react";
import {
  ACCEPTED_IMAGE_FORMATS_MESSAGE,
  IMAGE_FILE_ACCEPT,
  buildImageOrder,
  createImagePreview,
  partitionImageFiles,
} from "../../../utils/imageFiles";

const ExpandableText = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const LIMIT = 150;

  if (!text) return "Não informado.";

  if (text.length <= LIMIT) {
    return <span style={{ whiteSpace: "pre-wrap" }}>{text}</span>;
  }

  return (
    <div>
      <span style={{ whiteSpace: "pre-wrap" }}>
        {expanded ? text : `${text.substring(0, LIMIT)}... `}
      </span>
      <button className="btn-show-more" onClick={() => setExpanded(!expanded)}>
        {expanded ? "Mostrar menos" : "Mostrar mais"}
      </button>
    </div>
  );
};

const monthOptions = [
  { value: 1, label: "Janeiro" },
  { value: 2, label: "Fevereiro" },
  { value: 3, label: "Março" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Maio" },
  { value: 6, label: "Junho" },
  { value: 7, label: "Julho" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Setembro" },
  { value: 10, label: "Outubro" },
  { value: 11, label: "Novembro" },
  { value: 12, label: "Dezembro" },
];

const leisureLabels = {
  has_full_leisure: "Lazer Completo",
  has_pool: "Piscina",
  is_furnished: "Mobiliado",
  has_edicule: "Com Edícula",
  is_seafront: "Frente ao Mar",
  has_sea_view: "Vista ao Mar",
  is_near_beach: "Próximo a Praia",
};

const inputBaseStyle =
  "inline-block border border-gray-300 rounded px-2 h-[30px] text-sm ml-2 align-middle";
const tailwindCheckboxBaseStyle =
  "inline-block border border-gray-300 rounded h-4 w-4 text-sm ml-2 mr-1.5 align-middle accent-indigo-800";

const maskPhone = (value) => {
  if (!value) return "";
  return value
    .replace(/\D/g, "")
    .slice(0, 11)
    .replace(/^(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2");
};

const InlineInput = ({
  name,
  value,
  onChange,
  className,
  style,
  placeholder = "",
  ...props
}) => {
  const combinedClassName = `${inputBaseStyle} ${className || ""}`;
  return (
    <input
      type="text"
      name={name}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      className={combinedClassName}
      style={style}
      {...props}
    />
  );
};

const InlineMoneyInput = ({ name, value, onChange, className }) => (
  <input
    type="text"
    name={name}
    value={formatCentsToBRL(value)}
    onChange={onChange}
    className={`${inputBaseStyle} ${className}`}
  />
);

const InlineSelect = ({ name, value, onChange, children, className }) => (
  <select
    name={name}
    value={value || ""}
    onChange={onChange}
    className={`${inputBaseStyle} ${className}`}
  >
    {children}
  </select>
);

const TailwindCheckbox = ({ name, checked, onChange, label, id }) => (
  <span className="inline-flex items-center ml-2 align-middle">
    <input
      type="checkbox"
      name={name}
      id={id}
      checked={!!checked}
      onChange={onChange}
      className={`${tailwindCheckboxBaseStyle}`}
    />
    <label htmlFor={id} className="text-sm select-none cursor-pointer">
      {label}
    </label>
  </span>
);

const CustomCheckbox = ({ name, checked, onChange, label, id }) => (
  <span className="inline-checkbox-item">
    <input
      type="checkbox"
      name={name}
      id={id}
      checked={!!checked}
      onChange={onChange}
    />
    <label htmlFor={id}>{label}</label>
  </span>
);

const InlineTextArea = ({ name, value, onChange, className }) => (
  <textarea
    name={name}
    value={value || ""}
    onChange={onChange}
    className={`textarea-outras-info ${className || ""}`}
  />
);

export default function RealeaseProperties() {
  const navigate = useNavigate();
  const [propertiesForApproval, setPropertiesForApproval] = useState([]);
  const [allRealEstates, setAllRealEstates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingPropertyId, setEditingPropertyId] = useState(null);
  const [editedItemData, setEditedItemData] = useState({});
  const [originalItemData, setOriginalItemData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingCardRef, setEditingCardRef] = useState(null);

  const [listaImagens, setListaImagens] = useState([]);
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);

  const { refetchProperties } = useProperty();

  const initialBairros = [
    "Forte",
    "Boqueirão",
    "Guilhermina",
    "Aviação",
    "Tupi",
    "Ocian",
    "Mirim",
    "Maracanã",
    "Caiçara",
    "Vilamar",
    "Jardim Real",
    "Imperador",
    "Flórida",
    "Solemar",
    "Sítio do Campo",
    "Tude Bastos",
    "Jardim Glória",
    "Vila Sonia",
    "Vila Antártica",
    "Balneário Maxland",
    "Tupiry",
    "Anhanguera",
    "Quietude",
    "Nova Mirim",
    "Ribeirópolis",
    "Jardim Trevo",
    "Balneário Esmeralda",
    "Samambaia",
    "Parque das Américas",
    "Jardim Melvi",
    "Jardim Princesa",
    "Cidade das Crianças",
  ];

  const [bairrosDisplayOptions, setBairrosDisplayOptions] = useState(
    initialBairros.map((b) => ({ key: b, text: b, value: b })),
  );

  useEffect(() => {
    const currentBairro = editedItemData.bairro;

    if (currentBairro) {
      setBairrosDisplayOptions((prevOptions) => {
        const exists = prevOptions.some(
          (option) => option.value === currentBairro,
        );

        if (!exists) {
          return [
            { key: currentBairro, text: currentBairro, value: currentBairro },
            ...prevOptions,
          ];
        }
        return prevOptions;
      });
    }
  }, [editedItemData.bairro]);

  const handleBairroAddition = (e, { value }) => {
    setBairrosDisplayOptions((prev) => [
      { key: value, text: value, value },
      ...prev,
    ]);
  };

  const handleBairroChangeDropdown = (e, { value }) => {
    setEditedItemData((prev) => ({ ...prev, bairro: value }));
  };

  const handleStartInlineEdit = (item, event) => {
    const itemWithBoolStatus = {
      ...item,
      contatos: Array.isArray(item.contatos) ? item.contatos : [],
      drivelinks: String(item.drivelinks || "")
        .replace(/[{}]/g, "")
        .trim(),
      complemento: item.complemento || "",
      anoEntregaImovel: item.anoEntregaImovel || "",
      mesEntregaImovel: item.mesEntregaImovel || "",
    };

    setEditingPropertyId(item.id);
    setEditedItemData(itemWithBoolStatus);
    setOriginalItemData(itemWithBoolStatus);

    const existingImageUrls = Array.isArray(item.imagens)
      ? item.imagens.filter(
          (img) => typeof img === "string" && img.startsWith("http"),
        )
      : [];

    const formatadas = existingImageUrls.map((url, index) => ({
      id: `old-${item.id}-${index}-${Date.now()}`,
      type: "existing",
      url: url,
    }));

    setListaImagens(formatadas);

    if (event) {
      setEditingCardRef(event.currentTarget.closest("li"));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;
    if (name === "cep") {
      newValue = value.replace(/-/g, "");
    }
    setEditedItemData((prev) => {
      let newData = { ...prev, [name]: newValue };
      if (name === "statusImovel" && value === "Revenda") {
        newData.is_lancamento = false;
      }
      if (name === "posicaoImovel" && value !== "Em construção") {
        newData.mesEntregaImovel = "";
        newData.anoEntregaImovel = "";
      }
      return newData;
    });
  };

  const handleAddContact = () => {
    setEditedItemData((prev) => ({
      ...prev,
      contatos: [...(prev.contatos || []), { name: "", cel: "" }],
    }));
  };

  const handleRemoveContact = (index) => {
    setEditedItemData((prev) => ({
      ...prev,
      contatos: prev.contatos.filter((_, i) => i !== index),
    }));
  };

  const handleContactChange = (index, event) => {
    const { name, value } = event.target;
    setEditedItemData((prev) => {
      const updatedContacts = prev.contatos.map((contact, i) => {
        if (i === index) {
          const finalValue = name === "cel" ? value.replace(/\D/g, "") : value;
          return { ...contact, [name]: finalValue };
        }
        return contact;
      });
      return { ...prev, contatos: updatedContacts };
    });
  };

  const handleMoneyChange = (e) => {
    const { name, value } = e.target;
    let rawValue = value.replace(/\D/g, "");
    if (rawValue === "" || !rawValue) rawValue = "0";
    setEditedItemData((prev) => ({ ...prev, [name]: rawValue }));
  };

  const handleCommissionChange = (e) => {
    const displayValue = e.target.value;
    let apiValue = displayValue;
    if (displayValue === "Fifty") {
      apiValue = 999;
    } else if (displayValue.endsWith("%")) {
      apiValue = parseFloat(displayValue.replace("%", "")) / 100;
    }
    setEditedItemData((prev) => ({ ...prev, comissao: apiValue }));
  };

  const handleRealEstateChange = (e) => {
    const newRealEstateId = e.target.value;
    const realEstate = allRealEstates.find(
      (re) => String(re.id) === newRealEstateId,
    );
    setEditedItemData((prev) => ({
      ...prev,
      real_estate_id: newRealEstateId,
      responsibleName: realEstate
        ? realEstate.nomeFantasia ||
          realEstate.nome ||
          realEstate.razaoSocial ||
          `${realEstate.full_name} - (${realEstate.type || "Não informado"})`
        : "Não encontrado",
      categoriaResponsavel:
        realEstate?.type === "Imobiliária"
          ? "Imobiliária/Parceria"
          : realEstate?.type || "",
    }));
  };

  const adicionarNovasImagens = (files) => {
    const { accepted, rejected } = partitionImageFiles(files);
    if (rejected.length > 0) {
      alert(`Alguns arquivos não são imagens suportadas. ${ACCEPTED_IMAGE_FORMATS_MESSAGE}`);
    }

    const novasImagensFormatadas = accepted.map((file, i) => ({
      id: `new-${Date.now()}-${i}`,
      type: "new",
      file: file,
      url: createImagePreview(file),
    }));
    setListaImagens((prev) => [...prev, ...novasImagensFormatadas]);
  };

  const handleImgUpload = (e) =>
    adicionarNovasImagens(Array.from(e.target.files));

  const handleDropImg = (e) => {
    e.preventDefault();
    adicionarNovasImagens(Array.from(e.dataTransfer.files));
  };

  const handleDragOverImg = (e) => e.preventDefault();

  const removeImage = (idToRemove) => {
    setListaImagens((prev) => prev.filter((img) => img.id !== idToRemove));
  };

  const handleDragStartItem = (index) => {
    setDraggedItemIndex(index);
  };

  const handleDragEnterItem = (index) => {
    if (draggedItemIndex === null || draggedItemIndex === index) return;
    setListaImagens((prev) => {
      const newList = [...prev];
      const draggedItem = newList[draggedItemIndex];
      newList.splice(draggedItemIndex, 1);
      newList.splice(index, 0, draggedItem);
      setDraggedItemIndex(index);
      return newList;
    });
  };

  const handleDragEndItem = () => {
    setDraggedItemIndex(null);
  };

  const handleSaveInlineEdit = async () => {
    const existingUrlsToSave = listaImagens
      .filter((img) => img.type === "existing")
      .map((img) => img.url);
    const newFilesToSave = listaImagens
      .filter((img) => img.type === "new")
      .map((img) => img.file);

    const hasNewImages = newFilesToSave.length > 0;
    const existingImagesChanged = true;

    if (!hasChanges && !hasNewImages && !existingImagesChanged) {
      setEditingPropertyId(null);
      return;
    }

    setIsSaving(true);
    const formDataToSend = new FormData();
    const dataToProcess = { ...editedItemData };

    if (dataToProcess.posicaoImovel !== "Em construção") {
      dataToProcess.mesEntregaImovel = "";
      dataToProcess.anoEntregaImovel = "";
    }

    const imagensOriginais = originalItemData.imagens || [];
    const imagensRemovidas = imagensOriginais.filter(
      (urlOriginal) => !existingUrlsToSave.includes(urlOriginal),
    );

    imagensRemovidas.forEach((url) => {
      formDataToSend.append("deleted_images", url);
    });

    for (const key in dataToProcess) {
      const moneyFields = [
        "valor",
        "iptu",
        "condominio",
        "valorFinanciamentoDireto",
      ];
      if (moneyFields.includes(key)) {
        const valueFromState = String(dataToProcess[key] || "0").replace(
          /\D/g,
          "",
        );
        formDataToSend.append(key, valueFromState);
      } else if (key !== "imagens" && key !== "contatos") {
        formDataToSend.append(key, dataToProcess[key] ?? "");
      }
    }

    if (dataToProcess.contatos && Array.isArray(dataToProcess.contatos)) {
      dataToProcess.contatos.forEach((contact) => {
        if (contact.name || contact.cel) {
          formDataToSend.append("contatos", JSON.stringify(contact));
        }
      });
    }

    if (existingUrlsToSave.length === 0) {
      formDataToSend.append("imagens", "");
    } else {
      existingUrlsToSave.forEach((url) =>
        formDataToSend.append("imagens", url),
      );
    }

    newFilesToSave.forEach((file) =>
      formDataToSend.append("new_image_files", file),
    );

    const ordemImagens = buildImageOrder(listaImagens);
    formDataToSend.append("ordem_imagens", JSON.stringify(ordemImagens));

    const token = localStorage.getItem("token");
    const url = `${baseUrl}/properties/${editingPropertyId}`;

    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: formDataToSend,
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          data.detail || data.error || "Erro ao atualizar imóvel",
        );
      }

      await refetchProperties();
      await fetchData();
      setEditingPropertyId(null);
      alert("Imóvel atualizado com sucesso!");
    } catch (error) {
      console.error("Erro ao salvar imóvel:", error);
      alert(`Falha ao atualizar o imóvel: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelInlineEdit = () => setEditingPropertyId(null);

  const approvePropertyAPI = (item) => {
    const data = {
      status: "approved",
      images: item.imagens,
      valor: String(item.valor || "0").replace(/\D/g, ""),
      iptu: String(item.iptu || "0").replace(/\D/g, ""),
      condominio: String(item.condominio || "0").replace(/\D/g, ""),
      valorFinanciamentoDireto: String(
        item.valorFinanciamentoDireto || "0",
      ).replace(/\D/g, ""),
      drivelinks: String(item.drivelinks || "")
        .replace(/[{}]/g, "")
        .trim(),
    };
    return updatePropertie(item.id, data);
  };

  const handleApproveOneProperty = async (item) => {
    if (!window.confirm("Deseja realmente LIBERAR este imóvel para o público?"))
      return;
    try {
      await approvePropertyAPI(item);
      await refetchProperties();
      setPropertiesForApproval((prev) => prev.filter((p) => p.id !== item.id));
      alert("Imóvel liberado com sucesso!");
    } catch (error) {
      console.error("Erro ao liberar imóvel:", error);
    }
  };

  const handleDeleteProperty = async (itemId) => {
    if (
      !window.confirm(
        "Tem certeza que deseja remover este imóvel permanentemente?",
      )
    )
      return;
    try {
      await deletePropertie(itemId);
      setPropertiesForApproval((prev) => prev.filter((p) => p.id !== itemId));
    } catch (error) {
      console.error("Erro ao remover imóvel:", error);
      alert("Erro ao remover o imóvel.");
    }
  };

  const handleDuplicateProperty = async (item) => {
    if (!window.confirm("Deseja realmente DUPLICAR este imóvel?")) return;

    try {
      setIsLoading(true);

      const formDataToSend = new FormData();

      const currentUserId = localStorage.getItem("currentUserId");
      if (currentUserId) {
        formDataToSend.append("created_by", currentUserId);
      }

      const fieldsToExclude = [
        "id",
        "_id",
        "created_at",
        "updated_at",
        "dataCadastro",
        "dataAlteracao",
        "responsibleName",
        "imagens",
        "contatos",
        "new_image_files",
        "files",
        "status",
        "drivelinks",
      ];

      Object.keys(item).forEach((key) => {
        if (fieldsToExclude.includes(key)) return;

        const value = item[key];
        if (value === null || value === undefined) return;

        formDataToSend.append(key, value);
      });

      formDataToSend.append("status", "pending");
      formDataToSend.append("is_lancamento", item.is_lancamento ? "1" : "0");

      if (Array.isArray(item.imagens)) {
        item.imagens.forEach((url) => {
          if (typeof url === "string") {
            formDataToSend.append("imagens", url);
          }
        });
      }

      if (Array.isArray(item.contatos)) {
        item.contatos.forEach((contact) => {
          if (contact.name || contact.cel) {
            formDataToSend.append("contatos", JSON.stringify(contact));
          }
        });
      }

      const cleanDriveLink = String(item.drivelinks || "")
        .replace(/[{}]/g, "")
        .trim();
      if (cleanDriveLink) {
        formDataToSend.append("drivelinks", cleanDriveLink);
      }

      const token = localStorage.getItem("token");
      const response = await fetch(`${baseUrl}/properties/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataToSend,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || data.error || "Erro ao duplicar imóvel");
      }

      await refetchProperties();
      await fetchData();
      alert("Imóvel duplicado com sucesso!");
    } catch (error) {
      console.error("Erro ao duplicar:", error);
      alert(`Erro ao duplicar o imóvel: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproveAllProperties = async () => {
    if (!window.confirm("Deseja LIBERAR TODOS os imóveis listados abaixo?"))
      return;
    try {
      await Promise.all(
        propertiesForApproval.map((p) => approvePropertyAPI(p)),
      );
      await refetchProperties();
      setPropertiesForApproval([]);
      alert("Todos os imóveis foram liberados!");
    } catch (error) {
      console.error("Erro ao liberar todos:", error);
      fetchData();
    }
  };

  const handleDeleteAllProperties = async () => {
    if (
      !window.confirm(
        "Tem certeza que deseja remover TODOS os imóveis desta lista? Essa ação não pode ser desfeita.",
      )
    )
      return;
    try {
      await Promise.all(
        propertiesForApproval.map((p) => deletePropertie(p.id)),
      );
      setPropertiesForApproval([]);
    } catch (error) {
      console.error("Erro ao remover todos:", error);
      alert("Erro ao remover os imóveis.");
    }
  };

  const apiToDisplayCommission = (v) => {
    const s = String(v).toLowerCase().trim();
    if (s === "0.04") return "4%";
    if (s === "0.05") return "5%";
    if (s === "0.06") return "6%";
    if (["999", "0", "fifty"].includes(s)) return "Fifty";
    return "";
  };

  const formatarComissao = (v) => {
    if (v == null || v === "") return "Não informado";
    if (Number(v) === 999 || String(v).toLowerCase().trim() === "fifty")
      return "Fifty";
    const num = parseFloat(v);
    if (isNaN(num)) return v;
    return num < 1 ? `${(num * 100).toFixed(0)}%` : `${num}%`;
  };

  useEffect(() => {
    const cep = editedItemData.cep
      ? String(editedItemData.cep).replace(/\D/g, "")
      : "";

    const originalCep = originalItemData.cep
      ? String(originalItemData.cep).replace(/\D/g, "")
      : "";

    if (cep.length === 8 && cep !== originalCep) {
      fetch(`https://viacep.com.br/ws/${cep}/json/`)
        .then((res) => res.json())
        .then((data) => {
          if (!data.erro)
            setEditedItemData((prev) => ({
              ...prev,
              logradouro: data.logradouro || "",
              bairro: data.bairro || prev.bairro,
              cidade: data.localidade || "",
              estado: data.uf || "",
              complemento: data.complemento || prev.complemento,
            }));
        })
        .catch((err) => console.error("Erro no CEP:", err));
    }
  }, [editedItemData.cep, originalItemData.cep]);

  async function fetchData() {
    setIsLoading(true);
    try {
      const storedUserId = localStorage.getItem("currentUserId");
      const storedUserRole = localStorage.getItem("userRole");

      const [props, rEstates] = await Promise.all([
        listProperties(1, 1000),
        listRealEstates(),
      ]);
      setAllRealEstates(rEstates);
      const rEstatesMap = rEstates.reduce(
        (map, re) => ({ ...map, [re.id]: re }),
        {},
      );

      const filteredProps = props.filter((p) => {
        if (p?.status !== "pending") return false;

        const roleClean = String(storedUserRole).trim().toLowerCase();

        if (roleClean === "admin") {
          return true;
        }

        return String(p.created_by) === String(storedUserId);
      });

      setPropertiesForApproval(
        filteredProps.map((p) => {
          const reData = rEstatesMap[p.real_estate_id];
          const respName = reData
            ? reData.nomeFantasia ||
              reData.nome ||
              reData.razaoSocial ||
              `${reData.full_name} - (${reData.type || "Não informado"})`
            : "Não encontrado";
          const norm = (v) => {
            const c = String(v).toLowerCase().trim();
            if (["5.0", "5"].includes(c)) return 0.05;
            if (["6.0", "6"].includes(c)) return 0.06;
            if (["999", "0", "fifty"].includes(c)) return 999;
            return parseFloat(c) || v;
          };
          let finalContatos = [];
          if (p.contatos && typeof p.contatos === "string") {
            try {
              let parsedData = JSON.parse(p.contatos);
              if (
                Array.isArray(parsedData) &&
                parsedData.length > 0 &&
                Array.isArray(parsedData[0])
              ) {
                finalContatos = parsedData[0];
              } else if (Array.isArray(parsedData)) {
                finalContatos = parsedData;
              }
            } catch (e) {
              console.error("Erro decode JSON contatos:", p.contatos, e);
            }
          } else if (Array.isArray(p.contatos)) {
            finalContatos =
              p.contatos.length > 0 && Array.isArray(p.contatos[0])
                ? p.contatos[0]
                : p.contatos;
          }
          return {
            ...p,
            contatos: finalContatos,
            responsibleName: respName,
            valor: p.valor,
            iptu: p.iptu,
            condominio: p.condominio,
            valorFinanciamentoDireto: p.valorFinanciamentoDireto,
            parcelasFinanciamentoDireto: p.parcelasFinanciamentoDireto,
            info_condicao_financiamento: p.info_condicao_financiamento,
            comissao: norm(p.comissao),
            is_lancamento: !!p.is_lancamento,
            permuta: !!p.permuta,
            financiamentoDireto: !!p.financiamentoDireto,
            has_full_leisure: !!p.has_full_leisure,
            has_pool: !!p.has_pool,
            is_furnished: !!p.is_furnished,
            has_edicule: !!p.has_edicule,
            is_seafront: !!p.is_seafront,
            has_sea_view: !!p.has_sea_view,
            is_near_beach: !!p.is_near_beach,
            complemento: p.complemento,
            anoEntregaImovel: p.anoEntregaImovel,
            mesEntregaImovel: p.mesEntregaImovel,
          };
        }),
      );
    } catch (err) {
      console.error("Erro ao buscar dados:", err);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const hasDataChanges =
      Object.keys(originalItemData).length > 0 &&
      JSON.stringify(originalItemData) !== JSON.stringify(editedItemData);

    const existingUrlsNow = listaImagens
      .filter((img) => img.type === "existing")
      .map((img) => img.url);
    const hasNewImages = listaImagens.some((img) => img.type === "new");

    const hasImageChanges =
      hasNewImages ||
      JSON.stringify(existingUrlsNow) !==
        JSON.stringify(originalItemData.imagens || []);

    setHasChanges(hasDataChanges || hasImageChanges);
  }, [editedItemData, originalItemData, listaImagens]);

  useEffect(() => {
    if (editingPropertyId === null && editingCardRef) {
      editingCardRef.scrollIntoView({ behavior: "smooth", block: "start" });
      setEditingCardRef(null);
    }
  }, [editingPropertyId, editingCardRef]);

  if (isLoading)
    return (
      <p style={{ textAlign: "center", padding: "40px" }}>Carregando...</p>
    );

  const isRevenda = editedItemData.statusImovel === "Revenda";
  const quantityOptions = ["", 0, 1, 2, 3, 4, 5];

  return (
    <>
      <h1 className="page-title">LIBERAÇÃO DE IMÓVEIS</h1>
      <section className="section-properties-release">
        {propertiesForApproval.length > 0 ? (
          <div className="top-section-properties-release">
            <h3>TOTAL: {propertiesForApproval.length} IMÓVEIS</h3>
            <div>
              <button
                onClick={handleDeleteAllProperties}
                style={{ backgroundColor: "#b40000", color: "white" }}
              >
                REMOVER TODOS
              </button>
              <button onClick={handleApproveAllProperties}>
                LIBERAR TODOS
              </button>
            </div>
          </div>
        ) : (
          <div className="undefined-mensage">
            <p>
              Tudo certo! Nenhum imóvel para lançamento! <BsEmojiGrin />
            </p>
          </div>
        )}
        <ul>
          {propertiesForApproval.map((item) => (
            <li key={item.id}>
              {editingPropertyId === item.id ? (
                <>
                  <div className="infos-property-release">
                    <h3>
                      {item.tipoImovel === "Apartamento"
                        ? "APTO. "
                        : `${(item.tipoImovel || "IMÓVEL").toUpperCase()} `}
                      {item.dorms != null && item.dorms > 0
                        ? item.dorms === 1
                          ? `${item.dorms} DORMITÓRIO - `
                          : `${item.dorms} DORMITÓRIOS - `
                        : ""}
                      {item.nomeResidencial
                        ? `${item.nomeResidencial.toUpperCase()} `
                        : ""}
                      {` - ${(item.bairro || "Não informado.").toUpperCase()}`}
                    </h3>

                    <h3>IMAGENS DO IMÓVEL</h3>
                    <div className="image-upload-section">
                      <label
                        htmlFor={`fileInputImg-${item.id}`}
                        className="drop-area"
                        onDrop={handleDropImg}
                        onDragOver={handleDragOverImg}
                      >
                        <input
                          type="file"
                          id={`fileInputImg-${item.id}`}
                          multiple
                          accept={IMAGE_FILE_ACCEPT}
                          onChange={handleImgUpload}
                          style={{ display: "none" }}
                        />
                        SELECIONE IMAGENS OU ARRASTE E SOLTE
                        <small>{ACCEPTED_IMAGE_FORMATS_MESSAGE}</small>
                      </label>

                      <div className="image-preview">
                        {listaImagens.map((imgItem, index) => (
                          <div
                            key={imgItem.id}
                            className={`image-container ${draggedItemIndex === index ? "dragging" : ""}`}
                            draggable
                            onDragStart={() => handleDragStartItem(index)}
                            onDragEnter={() => handleDragEnterItem(index)}
                            onDragEnd={handleDragEndItem}
                            onDragOver={(e) => e.preventDefault()}
                            title="Arraste para reordenar"
                          >
                            <img
                              src={imgItem.url}
                              alt={`Preview ${index + 1}`}
                              className="preview-img"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(imgItem.id)}
                              className="btn-remove-file"
                            >
                              x
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <h3>INFORMAÇÕES DO IMÓVEL</h3>
                    <p>
                      <span>
                        <b>Tipo de Imóvel:</b>
                        <InlineSelect
                          name="tipoImovel"
                          value={editedItemData.tipoImovel || ""}
                          onChange={handleInputChange}
                          className="w-48"
                        >
                          <option value="">Selecione</option>
                          <option value="Casa">Casa</option>
                          <option value="Apartamento">Apartamento</option>
                          <option value="Casa em Condomínio">
                            Casa em Condomínio
                          </option>
                          <option value="Terreno">Terreno</option>
                        </InlineSelect>
                      </span>
                      <span>
                        <b>Área:</b>
                        <InlineInput
                          name="area"
                          value={editedItemData.area || ""}
                          onChange={handleInputChange}
                          className="w-28"
                          placeholder="m²"
                        />
                      </span>
                      {!isRevenda && (
                        <span>
                          <TailwindCheckbox
                            name="is_lancamento"
                            id={`l-${item.id}`}
                            checked={!!editedItemData.is_lancamento}
                            onChange={handleInputChange}
                            label="Lançamento"
                          />
                        </span>
                      )}
                      <span>
                        <b>Nome do Residencial:</b>
                        <InlineInput
                          name="nomeResidencial"
                          value={editedItemData.nomeResidencial || ""}
                          onChange={handleInputChange}
                          className="w-100"
                        />
                      </span>
                      <span>
                        <b>Status:</b>
                        <InlineSelect
                          name="statusImovel"
                          value={editedItemData.statusImovel || ""}
                          onChange={handleInputChange}
                          className="w-32"
                        >
                          <option value="Venda">Venda</option>
                          <option value="Revenda">Revenda</option>
                          <option value="Locação">Locação</option>
                        </InlineSelect>
                      </span>
                      <span>
                        <b>Posição:</b>
                        <InlineSelect
                          name="posicaoImovel"
                          value={editedItemData.posicaoImovel || ""}
                          onChange={handleInputChange}
                          className="w-40"
                        >
                          <option value="">Selecione</option>
                          <option value="Entregue">Entregue</option>
                          <option value="Em construção">Em construção</option>
                        </InlineSelect>
                        {editedItemData.posicaoImovel === "Em construção" && (
                          <>
                            <InlineSelect
                              name="mesEntregaImovel"
                              value={editedItemData.mesEntregaImovel || ""}
                              onChange={handleInputChange}
                              className="w-32 ml-2"
                            >
                              <option value="">Mês</option>
                              {monthOptions.map((m) => (
                                <option key={m.value} value={m.value}>
                                  {m.label}
                                </option>
                              ))}
                            </InlineSelect>

                            <InlineSelect
                              name="anoEntregaImovel"
                              value={editedItemData.anoEntregaImovel || ""}
                              onChange={handleInputChange}
                              className="w-24 ml-2"
                            >
                              <option value="">Ano</option>
                              {Array.from(
                                { length: 8 },
                                (_, i) => new Date().getFullYear() + i,
                              ).map((year) => (
                                <option key={year} value={year}>
                                  {year}
                                </option>
                              ))}
                            </InlineSelect>
                          </>
                        )}
                      </span>
                    </p>

                    <p>
                      <span>
                        <b>CEP</b>
                        <InlineInput
                          name="cep"
                          value={editedItemData.cep || ""}
                          onChange={handleInputChange}
                          className="w-28"
                          placeholder="CEP"
                        />
                      </span>
                      <span>
                        <b>Endereço</b>
                        <InlineInput
                          name="logradouro"
                          value={editedItemData.logradouro || ""}
                          onChange={handleInputChange}
                          className="w-72"
                          placeholder="Endereço"
                        />
                      </span>
                      <span>
                        <b>Número</b>
                        <InlineInput
                          name="numero"
                          value={editedItemData.numero || ""}
                          onChange={handleInputChange}
                          className="w-20"
                          placeholder="Nº"
                        />
                      </span>
                      <span>
                        <b>Complemento</b>
                        <InlineInput
                          name="complemento"
                          value={editedItemData.complemento || ""}
                          onChange={handleInputChange}
                          className="w-32"
                          placeholder="Apto/Bloco"
                        />
                      </span>
                      <span>
                        <b>Bairro</b>
                        <Dropdown
                          options={bairrosDisplayOptions}
                          placeholder="Selecione"
                          search
                          selection
                          allowAdditions
                          value={editedItemData.bairro}
                          onAddItem={handleBairroAddition}
                          onChange={handleBairroChangeDropdown}
                          className="custom-tiny-dropdown"
                        />
                      </span>
                      <span>
                        <b>Cidade</b>
                        <InlineInput
                          name="cidade"
                          value={editedItemData.cidade || ""}
                          onChange={handleInputChange}
                          className="w-32"
                          placeholder="Cidade"
                        />
                      </span>
                      <span>
                        <b>Estado</b>
                        <InlineInput
                          name="estado"
                          value={editedItemData.estado || ""}
                          onChange={handleInputChange}
                          className="w-16"
                          placeholder="UF"
                        />
                      </span>
                    </p>

                    <h3>INFORMAÇÕES DO RESPONSÁVEL PELO IMÓVEL</h3>
                    <p>
                      <span>
                        <b>Responsável:</b>
                        <InlineSelect
                          name="real_estate_id"
                          value={editedItemData.real_estate_id || ""}
                          onChange={handleRealEstateChange}
                          className="w-72"
                        >
                          <option value="">Selecione</option>
                          {allRealEstates.map((re) => (
                            <option key={re.id} value={re.id}>
                              {re.nomeFantasia ||
                                re.nome ||
                                re.razaoSocial ||
                                `${re.full_name} - (${re.type || "Não informado"})`}
                            </option>
                          ))}
                        </InlineSelect>
                      </span>
                      <span>
                        <b>Comissão:</b>
                        <InlineSelect
                          name="comissao"
                          value={apiToDisplayCommission(
                            editedItemData.comissao,
                          )}
                          onChange={handleCommissionChange}
                          className="w-32"
                        >
                          <option value="">Selecione</option>
                          <option value="4%">4%</option>
                          <option value="5%">5%</option>
                          <option value="6%">6%</option>
                          <option value="Fifty">Fifty</option>
                        </InlineSelect>
                      </span>
                    </p>

                    <h3>CONTATOS</h3>
                    <div className="flex flex-col gap-2">
                      {editedItemData.contatos &&
                        editedItemData.contatos.map((contact, index) => (
                          <p key={index} className="flex items-center gap-2">
                            <span>
                              <b>Contato {index + 1}:</b>
                            </span>
                            <span>
                              <b>Nome:</b>
                              <InlineInput
                                name="name"
                                value={contact.name || ""}
                                onChange={(e) => handleContactChange(index, e)}
                                className="w-60"
                                placeholder="Nome"
                              />
                            </span>
                            <span>
                              <b>Número:</b>
                              <InlineInput
                                name="cel"
                                value={maskPhone(contact.cel || "")}
                                onChange={(e) => handleContactChange(index, e)}
                                className="w-40"
                                placeholder="Celular"
                                maxLength={15}
                              />
                            </span>
                            <span>
                              <button
                                type="button"
                                onClick={() => handleRemoveContact(index)}
                                className="text-red-600 hover:text-red-800 p-2"
                                title="Remover Contato"
                              >
                                <FaRegTrashAlt />
                              </button>
                            </span>
                          </p>
                        ))}
                      <div className="mt-1">
                        <button
                          type="button"
                          onClick={handleAddContact}
                          className="text-sm bg-indigo-800 text-white py-1.5 px-3 rounded hover:bg-indigo-900"
                        >
                          <FaPlus />
                        </button>
                      </div>
                    </div>

                    <h3>VALORES E MÉTODOS DE PAGAMENTOS</h3>
                    <p>
                      <span>
                        <b>Valor:</b>
                        <InlineMoneyInput
                          name="valor"
                          value={editedItemData.valor}
                          onChange={handleMoneyChange}
                          className="w-40"
                        />
                      </span>
                      <span>
                        <b>IPTU:</b>
                        <InlineMoneyInput
                          name="iptu"
                          value={editedItemData.iptu}
                          onChange={handleMoneyChange}
                          className="w-32"
                        />
                      </span>
                      <span>
                        <b>Condomínio:</b>
                        <InlineMoneyInput
                          name="condominio"
                          value={editedItemData.condominio}
                          onChange={handleMoneyChange}
                          className="w-32"
                        />
                      </span>
                    </p>
                    <p>
                      <span>
                        <b>Métodos de Pagamento:</b>
                      </span>
                      <div className="inline-checkbox-values-and-payment-methods">
                        <CustomCheckbox
                          name="permuta"
                          id={`p-${item.id}`}
                          checked={!!editedItemData.permuta}
                          onChange={handleInputChange}
                          label="Permite Permuta"
                        />
                        <CustomCheckbox
                          name="financiamentoDireto"
                          id={`fd-${item.id}`}
                          checked={!!editedItemData.financiamentoDireto}
                          onChange={handleInputChange}
                          label="Financiamento Direto"
                        />
                      </div>
                      {!!editedItemData.financiamentoDireto && (
                        <div className="fd-inputs-container">
                          <span>
                            <b>Valor (FD):</b>
                            <InlineMoneyInput
                              name="valorFinanciamentoDireto"
                              value={editedItemData.valorFinanciamentoDireto}
                              onChange={handleMoneyChange}
                              className="w-40"
                            />
                          </span>
                          <span>
                            <b>Parcelas (FD):</b>
                            <InlineInput
                              name="parcelasFinanciamentoDireto"
                              value={
                                editedItemData.parcelasFinanciamentoDireto || ""
                              }
                              onChange={handleInputChange}
                              className="w-24"
                              placeholder="Qtd."
                              type="number"
                            />
                          </span>
                          <span className="w-full">
                            <b>Info. Adicional Financiamento:</b>
                            <InlineTextArea
                              name="info_condicao_financiamento"
                              value={
                                editedItemData.info_condicao_financiamento || ""
                              }
                              onChange={handleInputChange}
                              className="mt-1"
                            />
                          </span>
                        </div>
                      )}
                    </p>

                    <h3>CARACTERÍSTICAS DO IMÓVEL</h3>
                    <p>
                      <span>
                        <b>Dorms:</b>
                        <InlineSelect
                          name="dorms"
                          value={editedItemData.dorms ?? ""}
                          onChange={handleInputChange}
                          className="w-24"
                        >
                          {quantityOptions.map((option) => (
                            <option key={`dorms-${option}`} value={option}>
                              {option === "" ? "Não informado" : option}
                            </option>
                          ))}
                        </InlineSelect>
                      </span>
                      <span>
                        <b>WC:</b>
                        <InlineSelect
                          name="banheiros"
                          value={editedItemData.banheiros ?? ""}
                          onChange={handleInputChange}
                          className="w-24"
                        >
                          {quantityOptions.map((option) => (
                            <option key={`wc-${option}`} value={option}>
                              {option === "" ? "Não informado" : option}
                            </option>
                          ))}
                        </InlineSelect>
                      </span>
                      <span>
                        <b>Suítes:</b>
                        <InlineSelect
                          name="suites"
                          value={editedItemData.suites ?? ""}
                          onChange={handleInputChange}
                          className="w-24"
                        >
                          {quantityOptions.map((option) => (
                            <option key={`suites-${option}`} value={option}>
                              {option === "" ? "Não informado" : option}
                            </option>
                          ))}
                        </InlineSelect>
                      </span>
                      <span>
                        <b>Garagens:</b>
                        <InlineSelect
                          name="garagens"
                          value={editedItemData.garagens ?? ""}
                          onChange={handleInputChange}
                          className="w-24"
                        >
                          {quantityOptions.map((option) => (
                            <option key={`garagens-${option}`} value={option}>
                              {option === "" ? "Não informado" : option}
                            </option>
                          ))}
                        </InlineSelect>
                      </span>
                    </p>

                    <h3>CARACTERÍSTICAS DE LAZER</h3>
                    <div
                      className="inline-checkbox-group"
                      style={{ marginTop: "1rem" }}
                    >
                      {Object.keys(leisureLabels).map((key) => (
                        <CustomCheckbox
                          key={key}
                          name={key}
                          checked={!!editedItemData[key]}
                          onChange={handleInputChange}
                          id={`${key}-${item.id}`}
                          label={leisureLabels[key]}
                        />
                      ))}
                    </div>

                    <h3>OUTRAS INFORMAÇÕES</h3>
                    <p>
                      <span>
                        <b>Localização das Chaves:</b>
                        <InlineInput
                          name="chaves"
                          value={editedItemData.chaves || ""}
                          onChange={handleInputChange}
                          className="w-96"
                        />
                      </span>
                    </p>
                    <p>
                      <span>
                        <b>Link do Drive:</b>
                        <input
                          type="text"
                          name="drivelinks"
                          value={editedItemData.drivelinks || ""}
                          onChange={handleInputChange}
                          className="input-drivelinks"
                        />
                      </span>
                    </p>
                    <p>
                      <span className="w-full">
                        <b>Outras Informações (Notas):</b>
                        <InlineTextArea
                          name="outrasInfo"
                          value={editedItemData.outrasInfo || ""}
                          onChange={handleInputChange}
                          className="mt-1"
                        />
                      </span>
                    </p>
                  </div>
                  <div className="bts-section-properties-release editing-mode">
                    <button
                      className="cancel"
                      onClick={handleCancelInlineEdit}
                      disabled={isSaving}
                    >
                      CANCELAR
                    </button>
                    <button
                      className="save"
                      onClick={handleSaveInlineEdit}
                      disabled={!hasChanges || isSaving}
                    >
                      {isSaving ? "SALVANDO..." : "SALVAR"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="infos-property-release">
                    <button
                      className="btn-duplicate"
                      onClick={() => handleDuplicateProperty(item)}
                      title="Duplicar Imóvel"
                    >
                      <FaRegCopy />
                    </button>

                    <h3>
                      {item.tipoImovel === "Apartamento"
                        ? "APTO. "
                        : `${(item.tipoImovel || "IMÓVEL").toUpperCase()} `}
                      {item.dorms != null && item.dorms > 0
                        ? item.dorms === 1
                          ? `${item.dorms} DORMITário - `
                          : `${item.dorms} DORMITÓRIOS - `
                        : ""}
                      {item.nomeResidencial
                        ? `${item.nomeResidencial.toUpperCase()} `
                        : ""}
                      {` - ${(item.bairro || "Não informado.").toUpperCase()}`}
                    </h3>

                    <h3>INFORMAÇÕES DO IMÓVEL</h3>
                    <p>
                      <span>
                        <b>Tipo de Imóvel:</b>{" "}
                        {item.tipoImovel || "Não informado."}
                      </span>
                      <span>
                        <b>Área:</b>{" "}
                        {item.area ? `${item.area} m²` : "Não informado."}
                      </span>
                      {item.is_lancamento && (
                        <span style={{ color: "green", fontWeight: "bold" }}>
                          (LANÇAMENTO)
                        </span>
                      )}
                    </p>
                    <p>
                      <span>
                        <b>Nome do Residencial:</b>{" "}
                        {item.nomeResidencial || "Não informado."}
                      </span>
                      <span>
                        <b>Status:</b> {item.statusImovel || "Não informado."}
                      </span>
                      <span>
                        <b>Posição:</b> {item.posicaoImovel || "Não informado."}{" "}
                        {item.posicaoImovel === "Em construção" &&
                          item.anoEntregaImovel && (
                            <span className="ml-1" style={{ fontWeight: 500 }}>
                              - Entrega:{" "}
                              {item.mesEntregaImovel
                                ? monthOptions.find(
                                    (m) => m.value == item.mesEntregaImovel,
                                  )?.label + "/"
                                : ""}
                              {item.anoEntregaImovel}
                            </span>
                          )}
                      </span>
                    </p>

                    {(() => {
                      const {
                        logradouro,
                        numero,
                        complemento,
                        bairro,
                        cidade,
                        estado,
                        cep,
                      } = item;
                      const hasAnyAddress =
                        logradouro ||
                        numero ||
                        bairro ||
                        cidade ||
                        estado ||
                        cep;
                      return (
                        <p>
                          <span>
                            <b>Endereço:</b>
                            {hasAnyAddress
                              ? ` ${logradouro || "Rua não informada"} ${
                                  numero || "Nº não informado"
                                }${complemento ? ` - ${complemento}` : ""}, ${
                                  bairro || "Bairro não informado"
                                }, ${cidade || "Cidade não informada"}/${
                                  estado || "UF"
                                }, ${cep || "CEP não informado"}`
                              : " Não informado"}
                          </span>
                        </p>
                      );
                    })()}

                    <h3>INFORMAÇÕES DO RESPONSÁVEL PELO IMÓVEL</h3>
                    <p>
                      <span>
                        <b>Responsável:</b>{" "}
                        {item.responsibleName || "Não informado."}
                      </span>
                      <span>
                        <b>Comissão:</b> {formatarComissao(item.comissao)}
                      </span>
                    </p>

                    <h3>CONTATOS</h3>
                    {(() => {
                      const contatosValidos = Array.isArray(item.contatos)
                        ? item.contatos.filter(
                            (c) =>
                              (c.name && c.name.trim() !== "") ||
                              (c.cel && c.cel.trim() !== ""),
                          )
                        : [];
                      if (contatosValidos.length > 0) {
                        return contatosValidos.map((c, i) => (
                          <p key={i}>
                            <span>
                              <b>Contato {i + 1}:</b>{" "}
                              {c.name || "Nome não informado"} -{" "}
                              {maskPhone(c.cel) || "Celular não informado"}
                            </span>
                          </p>
                        ));
                      } else {
                        return (
                          <p>
                            <span>Não há contatos informados.</span>
                          </p>
                        );
                      }
                    })()}

                    <h3>VALORES E MÉTODOS DE PAGAMENTOS</h3>
                    <p>
                      <span>
                        <b>Valor:</b> {formatMoney(item.valor)}
                      </span>
                      <span>
                        <b>IPTU:</b> {formatMoney(item.iptu)}
                      </span>
                      {item.condominio != null && item.condominio !== "" && (
                        <span>
                          <b>Condomínio:</b> {formatMoney(item.condominio)}
                        </span>
                      )}
                    </p>
                    <p>
                      {(item.financiamentoDireto || item.permuta) && (
                        <span>
                          <b>Métodos de Pagamento:</b>
                          {item.permuta && " Permuta"}
                          {item.permuta && item.financiamentoDireto && " e"}
                          {item.financiamentoDireto && " Financiamento Direto"}
                          {item.financiamentoDireto && (
                            <span
                              style={{ marginLeft: "5px", color: "#1a237e" }}
                            >
                              | {formatMoney(item.valorFinanciamentoDireto)} (em{" "}
                              {item.parcelasFinanciamentoDireto || 0}x)
                            </span>
                          )}
                        </span>
                      )}
                      {!item.financiamentoDireto && !item.permuta && (
                        <span>
                          <b>Métodos de Pagamento:</b> Não informado.
                        </span>
                      )}
                    </p>
                    {item.info_condicao_financiamento && (
                      <p>
                        <span>
                          <b>Outras Informações (Pagamento):</b>
                          <div style={{ marginTop: "8px", whiteSpace: "pre-wrap" }}>
                            <ExpandableText text={item.info_condicao_financiamento} />
                          </div>
                        </span>
                      </p>
                    )}

                    <h3>CARACTERÍSTICAS DO IMÓVEL</h3>
                    {item.dorms != null ||
                    item.banheiros != null ||
                    item.suites != null ||
                    item.garagens != null ? (
                      <p>
                        {item.dorms != null && (
                          <span>
                            <b>Dorms:</b> {item.dorms}
                          </span>
                        )}
                        {item.banheiros != null && (
                          <span>
                            <b>WC:</b> {item.banheiros}
                          </span>
                        )}
                        {item.suites != null && (
                          <span>
                            <b>Suítes:</b> {item.suites}
                          </span>
                        )}
                        {item.garagens != null && (
                          <span>
                            <b>Garagens:</b> {item.garagens}
                          </span>
                        )}
                      </p>
                    ) : (
                      <p>
                        <span>Nenhuma característica numérica informada.</span>
                      </p>
                    )}

                    <h3>CARACTERÍSTICAS DE LAZER</h3>
                    {(() => {
                      const activeFeatures = Object.keys(leisureLabels)
                        .filter((key) => !!item[key])
                        .map((key) => leisureLabels[key]);
                      if (activeFeatures.length > 0)
                        return (
                          <p>
                            <span>{activeFeatures.join(" | ")}</span>
                          </p>
                        );
                      return (
                        <p>
                          <span>
                            Nenhuma característica de lazer informada.
                          </span>
                        </p>
                      );
                    })()}

                    <h3>OUTRAS INFORMAÇÕES</h3>
                    <p>
                      <span>
                        <b>Localização das Chaves:</b>{" "}
                        {item.chaves || "Não informado."}
                      </span>
                      {(() => {
                        const cleanLink = String(item.drivelinks || "")
                          .replace(/[{}]/g, "")
                          .trim();
                        return cleanLink && cleanLink.startsWith("http") ? (
                          <span>
                            <b>Link do Drive:</b>{" "}
                            <a
                              href={cleanLink}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Acessar
                            </a>
                          </span>
                        ) : (
                          <span>
                            <b>Link do Drive:</b> Não informado.
                          </span>
                        );
                      })()}
                    </p>
                    <p>
                      <span className="w-full">
                        <b>Outras Informações (Notas):</b>
                        <ExpandableText text={item.outrasInfo} />
                      </span>
                    </p>
                  </div>
                  <div className="bts-section-properties-release">
                    <button
                      onClick={() => handleDeleteProperty(item.id)}
                      style={{ backgroundColor: "#b40000", color: "white" }}
                    >
                      REMOVER
                    </button>
                    <button
                      onClick={(e) => handleStartInlineEdit(item, e)}
                      style={{ backgroundColor: "#ffee00", color: "black" }}
                    >
                      EDITAR
                    </button>
                    <button
                      onClick={() => handleApproveOneProperty(item)}
                      style={{ backgroundColor: "#0400ff", color: "white" }}
                    >
                      LIBERAR
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
